import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { US_STATES } from "@/lib/usStates";
import { toast } from "sonner";
import { FileText, ExternalLink, Download } from "lucide-react";

// RC49-CERT-008/009/010 — dealer-visible uploaded certificate files: current vs prior, open/download
const LIFE = { CURRENT: "badge-available", PENDING_REVIEW: "badge-reserved", REJECTED: "badge-sold", EXPIRED: "badge-reserved", REPLACED: "badge-neutral" };
const LIFE_LABEL = { CURRENT: "Current", PENDING_REVIEW: "Pending review", REJECTED: "Rejected", EXPIRED: "Expired", REPLACED: "Replaced", APPROVED: "Approved" };

export function CertificateFiles({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    api.get("/dealer/certificate/history").then((r) => setRows(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); else setRows([]); });
  }, [refreshKey]);

  const openFile = async (c, download) => {
    try {
      const res = await api.get(`/dealer/certificate/${c.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (download) { const a = document.createElement("a"); a.href = url; a.download = c.original_filename || "certificate"; a.click(); }
      else if (!window.open(url, "_blank", "noopener")) toast.error("Your browser blocked the file window — allow pop-ups or use Download.");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.status === 403 ? "You are not authorized to open this certificate file." : "The certificate file could not be opened. Try again or contact Zelligio.");
    }
  };

  if (denied) return <p className="mt-5 border-t border-border pt-5 text-xs text-warmgrey" data-testid="certificate-files-denied">Certificate documents are available to Dealer Admins and Location Managers.</p>;
  if (!rows?.length) return null;
  const current = rows.filter((c) => c.lifecycle === "CURRENT" || c.lifecycle === "PENDING_REVIEW");
  const prior = rows.filter((c) => !current.includes(c));
  const Row = ({ c }) => (
    <li className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm" data-testid={`certificate-file-${c.id}`}>
      <FileText className="h-4 w-4 shrink-0 text-terracotta" />
      <span className="min-w-0 flex-1 truncate font-medium text-charcoal">{c.original_filename || "Certificate"}</span>
      <span className={`badge-status ${LIFE[c.lifecycle] || "badge-neutral"}`} data-testid={`certificate-file-state-${c.id}`}>{LIFE_LABEL[c.lifecycle] || c.lifecycle}</span>
      <span className="text-xs text-warmgrey">{c.state ? US_STATES[c.state] || c.state : "Jurisdiction pending"}</span>
      {c.created_at && <span className="text-xs text-warmgrey whitespace-nowrap">uploaded {new Date(c.created_at).toLocaleDateString()}</span>}
      {c.expiration_date && <span className="text-xs text-warmgrey whitespace-nowrap">exp {c.expiration_date}</span>}
      <span className="flex gap-2">
        <button type="button" onClick={() => openFile(c, false)} className="inline-flex items-center gap-1 text-xs font-medium text-terracotta hover:underline" data-testid={`certificate-file-open-${c.id}`}><ExternalLink className="h-3 w-3" /> Open</button>
        <button type="button" onClick={() => openFile(c, true)} className="inline-flex items-center gap-1 text-xs font-medium text-terracotta hover:underline" data-testid={`certificate-file-download-${c.id}`}><Download className="h-3 w-3" /> Download</button>
      </span>
      {c.lifecycle === "REJECTED" && c.review_note && <p className="w-full text-xs text-status-sold" data-testid={`certificate-file-reason-${c.id}`}>Rejection reason: {c.review_note}</p>}
    </li>
  );
  return (
    <div className="mt-5 border-t border-border pt-5" data-testid="certificate-files">
      <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Your uploaded certificates</p>
      <p className="mt-1 text-[11px] text-warmgrey">Viewing a file does not change its review outcome — use Replace to submit a new certificate.</p>
      <ul className="mt-2 space-y-2">{current.map((c) => <Row key={c.id} c={c} />)}</ul>
      {prior.length > 0 && (
        <details className="mt-3" data-testid="certificate-files-prior">
          <summary className="cursor-pointer text-xs font-medium text-terracotta">Prior / expired versions ({prior.length})</summary>
          <ul className="mt-2 space-y-2">{prior.map((c) => <Row key={c.id} c={c} />)}</ul>
        </details>
      )}
    </div>
  );
}
